function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
// Lib Imports
import React from 'react';

// Core Imports
import Pagination from 'granite-admin/core/components/Pagination';
import NoRecordsPlaceholder from 'granite-admin/core/components/NoRecordsPlaceholder';
import { classes, makeTemplateComponent } from './utils';
import { IndeterminateCheckbox, ActionsCell, RowExpandCell, RowSubComponent, BulkActions, SortIcons } from './components';
const defaultProps = {
  // General
  data: [],
  setData: () => {},
  useControlledState: data => data,
  initialState: {},
  loading: false,
  showPagination: true,
  showPaginationTop: false,
  showPaginationBottom: true,
  showPageSizeOptions: true,
  pageSizeOptions: [5, 10, 20, 25, 50, 100],
  defaultPage: 0,
  defaultPageSize: 20,
  showPageJump: true,
  collapseOnSortingChange: true,
  collapseOnPageChange: true,
  collapseOnDataChange: true,
  freezeWhenExpanded: false,
  sortable: true,
  manualSortBy: true,
  multiSort: true,
  resizable: true,
  filterable: false,
  defaultSortDesc: false,
  defaultSorted: [],
  defaultFiltered: [],
  defaultResized: [],
  defaultExpanded: {},
  defaultColumn: {},
  selectable: false,
  draggable: false,
  expandable: false,
  columnsHideable: true,
  reorderable: false,
  utilsColumnPosition: undefined,
  // the common column for row dragging and expanding in cells and column visibility in header
  selectColumnPosition: undefined,
  // select column position
  actionColumnPosition: undefined,
  // actions column position
  visibilityColumnPosition: undefined,
  // if you want visibility column separated from row dragging/expanding column
  // Controlled State Props
  // page: undefined,
  // pageSize: undefined,
  // sorted: [],
  // filtered: [],
  // resized: [],
  // expanded: {},

  // Controlled State Callbacks
  // onPageChange: undefined,
  // onPageSizeChange: undefined,
  // onSortedChange: undefined,
  // onFilteredChange: undefined,
  // onResizedChange: undefined,
  // onExpandedChange: undefined,

  // Classes
  className: 'table',
  style: {},
  id: 'table',
  // Text
  previousText: 'Previous',
  nextText: 'Next',
  loadingText: 'Loading...',
  noDataText: 'No Records Found',
  pageText: 'Page',
  ofText: 'of',
  rowsText: 'rows',
  pageJumpText: 'jump to page',
  rowsSelectorText: 'rows per page',
  // Components
  TableComponent: ({
    children,
    className,
    ...rest
  }) => /*#__PURE__*/React.createElement("div", _extends({
    className: classes('rt-table', className),
    role: "grid"
    // tabIndex='0'
  }, rest), children),
  TheadComponent: makeTemplateComponent('rt-thead', 'Thead'),
  TbodyComponent: makeTemplateComponent('rt-tbody', 'Tbody'),
  TrGroupComponent: ({
    children,
    className,
    ...rest
  }) => /*#__PURE__*/React.createElement("div", _extends({
    className: classes('rt-tr-group', className),
    role: "rowgroup"
  }, rest), children),
  TrComponent: /*#__PURE__*/React.forwardRef(({
    children,
    className,
    ...rest
  }, ref) => /*#__PURE__*/React.createElement("div", _extends({
    ref: ref,
    className: classes(className, 'rt-tr'),
    role: "row"
  }, rest), children)),
  ThComponent: ({
    className,
    children,
    ...rest
  }) =>
  /*#__PURE__*/
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events
  React.createElement("div", _extends({
    className: classes('rt-th', className)
    // onClick={(e) => toggleSort && toggleSort(e)}
    ,
    role: "columnheader",
    tabIndex: "-1" // Resolves eslint issues without implementing keyboard navigation incorrectly
  }, rest), children),
  TdComponent: ({
    toggleSort,
    className,
    children,
    ...rest
  }) => /*#__PURE__*/React.createElement("div", _extends({
    className: classes('rt-td', className),
    role: "gridcell"
  }, rest), children),
  TfootComponent: makeTemplateComponent('rt-tfoot', 'Tfoot'),
  ExpanderComponent: ({
    isExpanded
  }) => /*#__PURE__*/React.createElement("div", {
    className: classes('rt-expander', isExpanded && '-open')
  }, "\u2022"),
  PivotValueComponent: ({
    subRows,
    value
  }) => /*#__PURE__*/React.createElement("span", null, value, " ", subRows && `(${subRows.length})`),
  AggregatedComponent: ({
    subRows,
    column
  }) => {
    const previewValues = subRows.filter(d => typeof d[column.id] !== 'undefined').map((row, i) =>
    /*#__PURE__*/
    // eslint-disable-next-line react/no-array-index-key
    React.createElement("span", {
      key: i
    }, row[column.id], i < subRows.length - 1 ? ', ' : ''));
    return /*#__PURE__*/React.createElement("span", null, previewValues);
  },
  PivotComponent: undefined,
  // this is a computed default generated using
  // the ExpanderComponent and PivotValueComponent at run-time in methods.js
  PaginationComponent: Pagination,
  PreviousComponent: undefined,
  NextComponent: undefined,
  LoadingComponent: ({
    className,
    loading,
    loadingText,
    ...rest
  }) => /*#__PURE__*/React.createElement("div", _extends({
    className: classes('-loading', {
      '-active': loading
    }, className)
  }, rest), /*#__PURE__*/React.createElement("div", {
    className: "-loading-inner"
  }, loadingText)),
  NoDataComponent: () => /*#__PURE__*/React.createElement(NoRecordsPlaceholder, {
    boxProps: {
      height: '200px',
      fill: 'horizontal'
    },
    textProps: {
      className: 'rt-noData',
      weight: 'bold'
    }
  }),
  // makeTemplateComponent('rt-noData', 'NoData'),
  ResizerComponent: makeTemplateComponent('rt-resizer', 'Resizer'),
  PadRowComponent: () => /*#__PURE__*/React.createElement("span", null, "\xA0"),
  SelectComponent: IndeterminateCheckbox,
  ActionsHeader: ({
    className
  }) => /*#__PURE__*/React.createElement("span", {
    className: className
  }, "\xA0"),
  ActionsCell: ActionsCell,
  UtilsHeader: () => /*#__PURE__*/React.createElement("span", null, "\xA0"),
  RowExpandCell: RowExpandCell,
  RowSubComponent: RowSubComponent,
  BulkActionsComponent: BulkActions,
  hideDefaultExpandable: false,
  hideDefaultDraggable: false,
  SortIconComponent: SortIcons,
  noHeader: false,
  showStrippedRows: true
};
export default defaultProps;